import { FC, useMemo } from "react";
import { UserGeneralProfile } from "@jugl-web/domain-resources/users/components/UserGeneralProfile";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { cx, useTranslations } from "@jugl-web/utils";
import { clearMessageBody } from "@web-src/modules/chats/utils";
import { getAttachmentsCountLabel } from "@web-src/modules/chats/utils/getAttachmentsCountLabel";
import { isPayloadVersion } from "@web-src/modules/chats/utils/isPayloadVersion";
import { parseChatMarkdown } from "@jugl-web/domain-resources/chat-bot/utils/parseChatMarkdown";
import { ReactComponent as ReplyIcon } from "./assets/reply-icon.svg";
import { useChatMessageBubble } from "../../providers/ChatMessageBubbleProvider/useChatMessageBubble";
import { AttachmentPreview } from "../../../AttachmentPreview";
import { VideoMeetingAttachment } from "../../../VideoMeetingAttachment";

export const ReplyMessagePreview: FC<{ onClick?: () => void }> = ({
  onClick,
}) => {
  const { isOutgoing, replyInfo, getAttachmentUrls, messagePayloadVersion } =
    useChatMessageBubble();
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();

  if (!replyInfo) {
    throw new Error("Reply message preview is not available");
  }

  const body = useMemo(() => {
    const messageBody = replyInfo.replyMsgBody || "";
    return messageBody ? clearMessageBody(messageBody) : "";
  }, [replyInfo.replyMsgBody]);

  const attachmentsText = useMemo(() => {
    const attachments = replyInfo.replyAttachments;

    if (attachments && attachments.length > 0) {
      if (attachments.length > 1) {
        return getAttachmentsCountLabel(
          attachments[0].type,
          attachments.length,
          t
        );
      }

      return (
        (isPayloadVersion(1, messagePayloadVersion)
          ? clearMessageBody(attachments[0].caption || "")
          : "") ||
        attachments[0].name ||
        t({
          id: "chats-page.file",
          defaultMessage: "File",
        })
      );
    }

    return "";
  }, [replyInfo.replyAttachments, messagePayloadVersion, t]);
  const { replyAttachment, preview } = useMemo(() => {
    const attachment = replyInfo.replyAttachments?.[0];
    if (attachment) {
      return {
        replyAttachment: attachment,
        preview: getAttachmentUrls(attachment).preview,
      };
    }

    return {
      replyAttachment: undefined,
      preview: undefined,
    };
  }, [getAttachmentUrls, replyInfo.replyAttachments]);

  return (
    <div
      className={cx(
        "mb-1 min-w-[200px] rounded bg-white p-[10px]",
        isOutgoing ? "bg-white" : "bg-dark-100",
        { "cursor-pointer": !!onClick }
      )}
      onClick={onClick}
    >
      <div className="flex max-h-14 min-h-[40px] shrink-0 gap-2">
        {!!replyAttachment && (
          <AttachmentPreview
            attachment={replyAttachment}
            preview={preview}
            color={isOutgoing ? "primary" : "white"}
            customIcon
            isOutgoing={isOutgoing}
          />
        )}
        <div className="flex h-full w-full flex-col justify-center gap-1 overflow-hidden">
          <div className="flex shrink-0 justify-between overflow-hidden text-ellipsis whitespace-nowrap">
            <span
              className={cx(
                "overflow-hidden text-[15px] font-medium text-ellipsis",
                isOutgoing ? "text-primary-500" : "text-[#B021F3]"
              )}
            >
              {replyInfo.replyUserName ? (
                replyInfo.replyUserName
              ) : (
                <UserGeneralProfile
                  userId={replyInfo.replyMsgUser}
                  entityId={entityId}
                >
                  {(user) => (
                    <>
                      {user.profile?.displayName ||
                        user.safeProfile.displayName}
                    </>
                  )}
                </UserGeneralProfile>
              )}
            </span>
            <ReplyIcon
              className="shrink-0"
              fill={isOutgoing ? "#2196F3" : "#B021F3"}
            />
          </div>
          {replyInfo.replyConference ? (
            <VideoMeetingAttachment
              conference={replyInfo.replyConference}
              isPreview
              isTile
              tileText={body || attachmentsText}
              className="max-w-[260px]"
            />
          ) : (
            <div className="line-clamp-1 overflow-hidden text-[13px] text-ellipsis whitespace-nowrap text-dark-800">
              {replyInfo.replyMsgBody
                ? parseChatMarkdown({
                    raws: replyInfo.replyMsgBody,
                    unstyledMention: true,
                  })
                : attachmentsText}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
